(() => {
  const bbbioInitializeForm = (bbbioRoot) => {
    if (bbbioRoot.dataset.bbbioInitialized === 'true') return;

    const bbbioForm = bbbioRoot.querySelector('[data-bbbio-service-form]');
    const bbbioSamplesContainer = bbbioRoot.querySelector('[data-bbbio-samples]');
    const bbbioSampleTemplate = bbbioRoot.querySelector('[data-bbbio-sample-template]');
    const bbbioStatus = bbbioRoot.querySelector('[data-bbbio-status]');
    const bbbioAddSampleButton = bbbioRoot.querySelector('[data-bbbio-add-sample]');
    const bbbioDownloadButton = bbbioRoot.querySelector('[data-bbbio-download-json]');
    const bbbioPrintButton = bbbioRoot.querySelector('[data-bbbio-print]');
    const bbbioAuthorizationDate = bbbioRoot.querySelector('[data-bbbio-authorization-date]');

    if (!bbbioForm || !bbbioSamplesContainer || !bbbioSampleTemplate || !bbbioStatus) return;
    bbbioRoot.dataset.bbbioInitialized = 'true';

    const bbbioScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    const bbbioSetStatus = (bbbioMessage, bbbioShouldScroll = false) => {
      bbbioStatus.textContent = bbbioMessage;
      bbbioStatus.dataset.bbbioVisible = 'true';
      if (bbbioShouldScroll) {
        bbbioStatus.scrollIntoView({ behavior: bbbioScrollBehavior, block: 'center' });
      }
    };

    const bbbioClearStatus = () => {
      bbbioStatus.textContent = '';
      bbbioStatus.dataset.bbbioVisible = 'false';
    };

    const bbbioUpdateServiceVisibility = (bbbioCard) => {
      const bbbioStrCheckbox = bbbioCard.querySelector('[data-bbbio-service="str"]');
      const bbbioMycoplasmaCheckbox = bbbioCard.querySelector('[data-bbbio-service="mycoplasma"]');
      bbbioCard.querySelector('.bbbio-str-fields').hidden = !bbbioStrCheckbox.checked;
      bbbioCard.querySelector('.bbbio-myco-fields').hidden = !bbbioMycoplasmaCheckbox.checked;
    };

    const bbbioRenumberSamples = () => {
      [...bbbioSamplesContainer.querySelectorAll('.bbbio-sample-card')].forEach((bbbioCard, bbbioIndex) => {
        const bbbioNumber = bbbioIndex + 1;
        bbbioCard.dataset.bbbioSampleIndex = String(bbbioNumber);
        bbbioCard.querySelector('.bbbio-sample-number').textContent = String(bbbioNumber);

        bbbioCard.querySelectorAll('[data-bbbio-field]').forEach((bbbioField) => {
          const bbbioFieldName = bbbioField.dataset.bbbioField;
          const bbbioFieldId = `bbbio-${bbbioRoot.id}-sample-${bbbioNumber}-${bbbioFieldName}`;
          bbbioField.id = bbbioFieldId;
          bbbioField.name = `samples[${bbbioNumber}][${bbbioFieldName}]`;
          const bbbioLabel = bbbioCard.querySelector(`[data-bbbio-label="${bbbioFieldName}"]`);
          if (bbbioLabel) bbbioLabel.htmlFor = bbbioFieldId;
        });

        bbbioCard.querySelectorAll('[data-bbbio-service]').forEach((bbbioField) => {
          const bbbioServiceName = bbbioField.dataset.bbbioService;
          bbbioField.name = `samples[${bbbioNumber}][services][]`;
          bbbioField.value = bbbioServiceName === 'str' ? 'Human STR Profiling' : 'Mycoplasma Testing';
        });
      });
    };

    const bbbioAddSample = () => {
      const bbbioFragment = bbbioSampleTemplate.content.cloneNode(true);
      const bbbioCard = bbbioFragment.querySelector('.bbbio-sample-card');

      bbbioCard.querySelector('.bbbio-remove-sample').addEventListener('click', () => {
        if (bbbioSamplesContainer.querySelectorAll('.bbbio-sample-card').length === 1) {
          bbbioSetStatus('At least one sample is required.', true);
          return;
        }
        bbbioCard.remove();
        bbbioRenumberSamples();
        bbbioClearStatus();
      });

      bbbioCard.querySelectorAll('[data-bbbio-service]').forEach((bbbioCheckbox) => {
        bbbioCheckbox.addEventListener('change', () => bbbioUpdateServiceVisibility(bbbioCard));
      });

      bbbioSamplesContainer.appendChild(bbbioFragment);
      bbbioRenumberSamples();
      bbbioUpdateServiceVisibility(bbbioSamplesContainer.lastElementChild);
    };

    const bbbioValidateServices = () => {
      const bbbioSubmissionServices = bbbioRoot.querySelectorAll('[data-bbbio-submission-services] input');
      if (![...bbbioSubmissionServices].some((bbbioCheckbox) => bbbioCheckbox.checked)) {
        bbbioSetStatus('Select at least one requested service for the submission.', true);
        return false;
      }

      for (const bbbioCard of bbbioSamplesContainer.querySelectorAll('.bbbio-sample-card')) {
        const bbbioHasService = [...bbbioCard.querySelectorAll('[data-bbbio-service]')]
          .some((bbbioCheckbox) => bbbioCheckbox.checked);
        if (!bbbioHasService) {
          bbbioCard.scrollIntoView({ behavior: bbbioScrollBehavior, block: 'center' });
          bbbioSetStatus('Each sample must have at least one requested service.');
          return false;
        }
      }
      return true;
    };

    const bbbioValidateForm = () => bbbioForm.reportValidity() && bbbioValidateServices();

    const bbbioCollectSubmission = () => {
      const bbbioFormData = new FormData(bbbioForm);
      const bbbioSubmission = {
        submittedAt: new Date().toISOString(),
        customer: {},
        services: bbbioFormData.getAll('submissionServices'),
        samples: [],
        shipping: {},
        authorization: {},
        laboratoryUseOnly: {}
      };

      [
        'organization', 'department', 'contactName', 'email', 'phone', 'customerAccount',
        'address', 'poNumber', 'quoteNumber', 'turnaround', 'reportFormat', 'reportRecipients'
      ].forEach((bbbioKey) => {
        bbbioSubmission.customer[bbbioKey] = bbbioFormData.get(bbbioKey) || '';
      });

      [...bbbioSamplesContainer.querySelectorAll('.bbbio-sample-card')].forEach((bbbioCard, bbbioIndex) => {
        const bbbioSample = { sampleNumber: bbbioIndex + 1, services: [] };
        bbbioCard.querySelectorAll('[data-bbbio-field]').forEach((bbbioField) => {
          bbbioSample[bbbioField.dataset.bbbioField] = bbbioField.value;
        });
        bbbioCard.querySelectorAll('[data-bbbio-service]:checked').forEach((bbbioField) => {
          bbbioSample.services.push(bbbioField.value);
        });
        bbbioSubmission.samples.push(bbbioSample);
      });

      ['shipDate', 'carrier', 'tracking', 'shipCondition', 'returnMaterial', 'disposition', 'shippingNotes']
        .forEach((bbbioKey) => {
          bbbioSubmission.shipping[bbbioKey] = bbbioFormData.get(bbbioKey) || '';
        });

      ['authorizedName', 'authorizedTitle', 'authorizationDate', 'electronicSignature']
        .forEach((bbbioKey) => {
          bbbioSubmission.authorization[bbbioKey] = bbbioFormData.get(bbbioKey) || '';
        });
      bbbioSubmission.authorization.attestAccurate = bbbioRoot.querySelector('[data-bbbio-attest-accurate]').checked;
      bbbioSubmission.authorization.attestIdentifiers = bbbioRoot.querySelector('[data-bbbio-attest-identifiers]').checked;
      bbbioSubmission.authorization.attestHazards = bbbioRoot.querySelector('[data-bbbio-attest-hazards]').checked;

      ['accessionNumber', 'receivedDate', 'receivedBy', 'condition', 'temperature', 'acceptance', 'labNotes']
        .forEach((bbbioKey) => {
          bbbioSubmission.laboratoryUseOnly[bbbioKey] = bbbioFormData.get(bbbioKey) || '';
        });
      return bbbioSubmission;
    };

    bbbioAddSampleButton.addEventListener('click', () => {
      bbbioAddSample();
      bbbioClearStatus();
    });

    bbbioPrintButton.addEventListener('click', () => window.print());

    bbbioDownloadButton.addEventListener('click', () => {
      if (!bbbioValidateForm()) return;
      const bbbioBlob = new Blob([JSON.stringify(bbbioCollectSubmission(), null, 2)], { type: 'application/json' });
      const bbbioUrl = URL.createObjectURL(bbbioBlob);
      const bbbioLink = document.createElement('a');
      bbbioLink.href = bbbioUrl;
      bbbioLink.download = `cell-authentication-submission-${Date.now()}.json`;
      document.body.appendChild(bbbioLink);
      bbbioLink.click();
      bbbioLink.remove();
      URL.revokeObjectURL(bbbioUrl);
      bbbioSetStatus('Submission JSON downloaded.', true);
    });

    bbbioForm.addEventListener('submit', (bbbioEvent) => {
      bbbioEvent.preventDefault();
      if (!bbbioValidateForm()) return;
      bbbioSetStatus('Form validated. This template currently creates a reviewable submission payload but does not transmit data. Connect the submit handler to your secure server or form platform before production use.', true);
    });

    bbbioForm.addEventListener('reset', () => {
      window.setTimeout(() => {
        bbbioSamplesContainer.innerHTML = '';
        bbbioAddSample();
        bbbioClearStatus();
        bbbioAuthorizationDate.valueAsDate = new Date();
      }, 0);
    });

    bbbioAuthorizationDate.valueAsDate = new Date();
    bbbioAddSample();
  };

  document.querySelectorAll('[data-bbbio-cell-authentication-form]').forEach(bbbioInitializeForm);
})();
